// src/components/elementsui/ProductDetails.js

import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import './ProductDetails.css';
import Preloader from '../common/preloader';

const ProductDetails = () => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const [product, setProduct] = useState(null);
  const navigate = useNavigate();
  const [selectedWeight, setSelectedWeight] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/products/${id}`);
        setProduct(response.data);
        if (response.data.weights && response.data.weights.length > 0) {
          setSelectedWeight(response.data.weights[0]);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      }
    };
    fetchProduct();
  }, [id, pathname]);

  if (!product) {
    return <Preloader />;
  }

  const handleWeightChange = (event) => {
    const selectedOption = product.weights.find((option) => option.weight === event.target.value);
    setSelectedWeight(selectedOption);
  };

  const handleSlideChange = (direction) => {
    if (direction === 'next') {
      setActiveSlide((prev) => (prev + 1) % product.images.length);
    } else if (direction === 'prev') {
      setActiveSlide((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  const handleAddToCart = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    if (selectedWeight) {
      try {
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/cart/add`,
          {
            productId: product._id,
            name: product.name,
            image: product.images[0],
            weight: selectedWeight.weight,
            price: selectedWeight.price,
            quantity: 1,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Error adding to cart:', error);
      }
    }
  };

  const selectedPrice = selectedWeight ? selectedWeight.price : 0;

  return (
    <div className="product-detail container py-4">
      {/* ✅ SEO Helmet: Dynamic product meta */}
      <Helmet>
        <title>{product.name} | binzal - Buy Online in Bihar</title>
        <meta
          name="description"
          content={`Buy ${product.name} online from binzal. Fresh groceries, vegetables, and essentials delivered fast across Bihar.`}
        />
        <meta
          name="keywords"
          content={`${product.name} online Bihar, buy ${product.name} Patna, grocery delivery binzal`}
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={`${product.name} | binzal`} />
        <meta property="og:description" content={`Order ${product.name} online with binzal and get it delivered quickly to your doorstep in Bihar.`} />
        <meta property="og:type" content="product" />
        <meta name="twitter:card" content="summary" />
      </Helmet>

      {/* Product display section starts here */}
      <div className="row">
        <div className="col-12 col-md-6">
          <div className="d-none d-md-block">
            <div className="main-image-container">
              <img
                src={product.images[activeSlide]}
                alt={product.name}
                className="img-fluid main-image"
              />
            </div>
            <div className="thumbnail-container d-flex mt-3">
              {product.images.map((img, index) => (
                <div
                  key={index}
                  className={`thumbnail ${index === activeSlide ? 'active' : ''}`}
                  onClick={() => setActiveSlide(index)}
                >
                  <img
                    src={img}
                    alt={`${product.name} Thumbnail ${index + 1}`}
                    className="img-fluid"
                  />
                </div>
              ))}
            </div>
            <div className="description-section">
              <h3 className="section-title">Product Description</h3>
              <p className="description-text">{product.description}</p>
            </div>
          </div>
          <div className="d-md-none">
            <div className="custom-slider">
              <div
                className="slider-wrapper"
                style={{ transform: `translateX(-${activeSlide * 100}%)` }}
              >
                {product.images.map((img, index) => (
                  <div key={index} className="slider-slide">
                    <img
                      src={img}
                      className="d-block w-100"
                      alt={`${product.name} ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
              <button className="slider-arrow prev-arrow" onClick={() => handleSlideChange('prev')}>
                <i className="bi bi-chevron-left"></i>
              </button>
              <button className="slider-arrow next-arrow" onClick={() => handleSlideChange('next')}>
                <i className="bi bi-chevron-right"></i>
              </button>
              <div className="slider-pagination">
                {product.images.map((_, index) => (
                  <button
                    key={index}
                    className={`pagination-dot ${index === activeSlide ? 'active' : ''}`}
                    onClick={() => setActiveSlide(index)}
                  ></button>
                ))}
              </div>
              <div className="slider-counter text-center">
                {`${activeSlide + 1} of ${product.images.length}`}
              </div>
            </div>
            <div className="description-section">
              <h3 className="section-title">Product Description</h3>
              <p className="description-text">{product.description}</p>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6">
          <h2 className="product-heading">{product.name}</h2>
          <div className="custom-weight-selector">
            <select
              className="weight-dropdown"
              value={selectedWeight ? selectedWeight.weight : ''}
              onChange={handleWeightChange}
            >
              {product.weights.map((option, index) => (
                <option key={index} value={option.weight}>
                  {`${option.weight} - ₹${option.price}`}
                </option>
              ))}
            </select>
          </div>
          <div className="price-section">
            <span className="price-text">
              <i className="bi bi-currency-rupee me-1"></i>
              {selectedPrice}
            </span>
            <span className="tax-text ms-2">(Inclusive of all taxes)</span>
          </div>
          <button className="btn add-to-cart" onClick={handleAddToCart}>
            Add <i className="bi bi-currency-rupee ms-1"></i>{selectedPrice}
          </button>
          <div className="why-shop-section">
            <h3 className="why-shop-title">Why shop from binzal?</h3>
            <div className="row">
              <div className="col-12 col-sm-6 col-md-12 mb-3">
                <div className="d-flex align-items-start">
                  <div className="icon-circle me-3">
                    <i className="bi bi-truck icon-style"></i>
                  </div>
                  <div>
                    <h5 className="feature-title">Superfast Delivery</h5>
                    <p className="feature-text">
                      Get your order delivered to your doorstep at the earliest from dark stores near you.
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-12 col-sm-6 col-md-12 mb-3">
                <div className="d-flex align-items-start">
                  <div className="icon-circle me-3">
                    <i className="bi bi-tag-fill icon-style"></i>
                  </div>
                  <div>
                    <h5 className="feature-title">Best Prices & Offers</h5>
                    <p className="feature-text">
                      Best price destination with offers directly from the manufacturers.
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-12 col-sm-6 col-md-12 mb-3">
                <div className="d-flex align-items-start">
                  <div className="icon-circle me-3">
                    <i className="bi bi-basket-fill icon-style"></i>
                  </div>
                  <div>
                    <h5 className="feature-title">Wide Assortment</h5>
                    <p className="feature-text">
                      Choose from 1000+ products across food, personal care, household & other categories.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
